import React from 'react';
import {
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  Stack,
  Typography,
  Box,
  Paper
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { Task } from '@/types';

interface TaskListProps {
  tasks: Task[];
  onEdit: (task: Task) => void;
  onDelete: (id: string) => void;
  onView: (task: Task) => void;
}

export const TaskList: React.FC<TaskListProps> = ({
  tasks,
  onEdit,
  onDelete,
  onView
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_process':
        return 'primary';
      case 'frozen':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  if (tasks.length === 0) {
    return (
      <Typography color="text.secondary" align="center" py={4}>
        No tasks found
      </Typography>
    );
  }

  return (
    <List>
      {tasks.map((task) => (
        <ListItem
          key={task.id}
          component={Paper}
          variant="outlined"
          sx={{ mb: 2, p: 2 }}
        >
          <Stack spacing={2} sx={{ width: '100%' }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
            >
              <Box>
                <Typography variant="subtitle1">
                  {task.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {task.description}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <IconButton
                  size="small"
                  onClick={() => onView(task)}
                  color="primary"
                >
                  <ViewIcon />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => onEdit(task)}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => onDelete(task.id)}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Stack>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={task.status.replace('_', ' ')}
                color={getStatusColor(task.status)}
                size="small"
              />
              <Chip
                label={task.priority}
                color={getPriorityColor(task.priority)}
                size="small"
                variant="outlined"
              />
              <Chip
                label={task.category}
                size="small"
                variant="outlined"
              />
              <Typography variant="caption" color="text.secondary">
                Due: {format(new Date(task.dueDate), 'PPP')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Assigned to: {task.assignedTo}
              </Typography>
            </Stack>

            {task.comments.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Latest comment:
                </Typography>
                <Typography variant="body2">
                  {task.comments[task.comments.length - 1].content}
                </Typography>
              </Box>
            )}
          </Stack>
        </ListItem>
      ))}
    </List>
  );
};